import type { AuditLabelResolver } from '@/features/audit/audit-humanizer'
import { type CreditStage, creditStageVariableToken } from './credit-expedient'

/**
 * EL DICCIONARIO DE LA BITÁCORA DEL EXPEDIENTE.
 *
 * ── QUÉ PROBLEMA RESUELVE ────────────────────────────────────────────────────
 *
 * La API sella las etiquetas al escribir la bitácora, pero eso empezó a pasar hace días: todo lo
 * anterior guarda sólo claves (`initial_contact`, `resolutionResult`, `in_review`) y el renglón
 * terminaba diciendo «Etapa initial_contact» — el modelo de datos en la cara del usuario final.
 *
 * La etiqueta que falta está a mano: el expediente que se está mirando declara sus etapas, sus
 * puntos y sus campos con nombre. Esto arma el mapa de claves→nombres UNA vez y se lo pasa al
 * humanizador, igual que la pantalla le pasa `resolveActor` para los ids de usuario.
 *
 * ── POR QUÉ DEVUELVE `undefined` Y NO LA CLAVE ───────────────────────────────
 *
 * Porque el humanizador tiene un fallback digno («una etapa») y necesita saber que no se resolvió.
 * Una plantilla que cambió de versión puede haber perdido una etapa vieja: en ese caso NADIE sabe
 * cómo se llamaba, y decirlo genérico es honesto — deletrear la clave no.
 */
export function creditAuditLabels(stages: readonly CreditStage[] | undefined): AuditLabelResolver {
  if (!stages || stages.length === 0) return {}

  const stageNames = new Map<string, string>()
  const items = new Map<string, string | null>()
  const fields = new Map<string, CreditFieldLabels | null>()

  for (const stage of stages) {
    if (stage.key) stageNames.set(stage.key, stage.name)

    for (const item of stage.items ?? []) {
      remember(items, itemPath(stage.key, item.key), item.label)
      remember(items, item.key, item.label)

      for (const field of item.fields ?? []) {
        const entry: CreditFieldLabels = { label: field.label, options: optionsOf(field) }
        remember(fields, fieldPath(stage.key, item.key, field.key), entry)
        // El TOKEN con el que las reglas del kernel nombran al campo: va por `order` de la etapa,
        // no por su clave (ver `creditStageVariableToken`). Sin esto, «por la regla
        // etapa4.resolutionResult» no resuelve contra nada.
        remember(fields, creditStageVariableToken(stage.order, field.key), entry)
        remember(fields, field.key, entry)
      }
    }
  }

  const fieldAt = (
    key: string,
    stageKey?: string,
    itemKey?: string
  ): CreditFieldLabels | undefined => {
    const exact = stageKey && itemKey ? fields.get(fieldPath(stageKey, itemKey, key)) : undefined
    return exact ?? fields.get(key) ?? undefined
  }

  return {
    stage: (key) => stageNames.get(key),
    item: (key, stageKey) =>
      (stageKey ? items.get(itemPath(stageKey, key)) : undefined) ?? items.get(key) ?? undefined,
    field: (key, stageKey, itemKey) => fieldAt(key, stageKey, itemKey)?.label,
    option: (value, key, stageKey, itemKey) => fieldAt(key, stageKey, itemKey)?.options.get(value)
  }
}

/** Un campo reducido a lo que la bitácora necesita nombrar: cómo se llama y qué guarda. */
type CreditFieldLabels = { label: string; options: Map<string, string> }

const itemPath = (stageKey: string, itemKey: string): string => `${stageKey} ${itemKey}`

const fieldPath = (stageKey: string, itemKey: string, fieldKey: string): string =>
  `${stageKey} ${itemKey} ${fieldKey}`

/**
 * Sólo una LISTA y una casilla guardan CLAVES en la bitácora; el resto de los valores la API ya
 * los escribe para una persona. Por eso el mapa se llena únicamente para esos dos.
 */
function optionsOf(field: CreditStage['items'][number]['fields'][number]): Map<string, string> {
  if (field.config.kind === 'select') {
    return new Map(field.config.options.map((option) => [option.value, option.label]))
  }
  if (field.config.kind === 'checkbox') {
    return new Map([
      ['true', 'Sí'],
      ['false', 'No']
    ])
  }
  return new Map()
}

/**
 * Guarda una clave que NO puede mentir.
 *
 * Las claves con ruta completa (`etapa · punto · campo`) son únicas por construcción, pero la
 * clave PELADA no: dos etapas pueden tener un `observaciones` cada una. Si dos cosas distintas
 * comparten la clave suelta, la clave queda irresoluble en vez de quedarse con la primera — el
 * humanizador cae a su texto genérico, preferible a colgarle a un renglón el nombre de OTRO campo.
 */
function remember<T extends string | CreditFieldLabels>(
  index: Map<string, T | null>,
  key: string,
  value: T
): void {
  if (!key) return
  const current = index.get(key)
  if (current === undefined) {
    index.set(key, value)
    return
  }
  // ⚠️ Se ENVENENA con `null`, no se borra: borrando, una tercera etapa con la etiqueta de la
  // primera volvería a poblar la clave y el conflicto reaparecería según el orden de las etapas.
  if (current === null || labelOf(current) !== labelOf(value)) index.set(key, null)
}

const labelOf = (value: string | CreditFieldLabels): string =>
  typeof value === 'string' ? value : value.label
